import {
    DataGrid,
    GridActionsCellItem, GridDensity,
    GridRowClassNameParams,
    GridRowParams, GridSlotProps,
    GridSlots,
    useGridApiRef,
} from "@mui/x-data-grid";
import * as React from "react";
import ToolbarContainer, {ToolbarContainerProps} from "@/widget/ProTable/ToolbarContainer";
import {GridColDef} from "@mui/x-data-grid/models/colDef/gridColDef";
import {AuthButtonVO} from "@/service/menu.s";
import {ReactNode, RefObject, useEffect, useMemo} from "react";
import {GridApiCommunity} from "@mui/x-data-grid/internals";
import {GridFeatureMode} from "@mui/x-data-grid/models/gridFeatureMode";
import NoResultsOverlay from "@/widget/NoResultsOverlay/NoResultsOverlay";
import {useGetAuthButtons} from "@/store/menu.c";
import isEmpty from "lodash/isEmpty";
import {useTipBox} from "@/widget/TipBox/TipBox";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import {useDeletes, useGlobalActions} from "@/store/global.c";
import {GridPaginationModel} from "@mui/x-data-grid/models/gridPaginationProps";
import {useForm, UseFormReturn} from "react-hook-form";
import {PaginationVO} from "@/model/global";
import {SlotDrawerHeaderProps, SlotDrawerProps} from "@/widget/DrawerModal/DrawerModal";
import {debounce, find} from "lodash";
import Box from "@mui/material/Box";
import type {GridDataSource} from "@mui/x-data-grid/models/gridDataSource";
import {FooterContainer, FooterContainerProps} from "@/widget/ProTable/FooterContainer";
import {defaultPageParams, removeEmptyValue} from "@/helper/constant";
import {GridColumnVisibilityModel} from "@mui/x-data-grid/hooks/features/columns/gridColumnsInterfaces";
import {useGetSearchParam} from "@/store/dataGrid.c";

declare module '@mui/x-data-grid' {
    interface ToolbarPropsOverrides extends ToolbarContainerProps {
        code: string
    }

    interface FooterPropsOverrides extends FooterContainerProps {
        code: string
        feet: TableFeetType
    }
}

export type BtnType<T> = {
    btn: AuthButtonVO;
    keys: number[];
    rows: T[];
    onSearch?: (params: any) => void;
};

export type TableFeetType = {
    custom?: boolean;
    hidden?: boolean
    leftFunc?: (rows: RefObject<GridApiCommunity | null>) => ReactNode;
}

interface ProTableProps<R extends { id: number | string }> {
    code?: string;
    columns: GridColDef<R>[];
    paginationMode?: GridFeatureMode;
    loading?: boolean;
    feet?: TableFeetType;
    pageSize?: number;
    autoHeight?: boolean;
    hover?: boolean
    rowId?: string
    btnCallback?: (params: BtnType<R>) => void;
    viewCallback?: (params: BtnType<R>) => void;
    editCallback?: (params: BtnType<R>) => void;
    onSearch?: (params: any) => void;
    data?: PaginationVO<R>;
    columnVisibilityModel?: GridColumnVisibilityModel
    dialogBtnCallback?: (params: BtnType<R>) => void;
    drawerProps?: { [key: string]: SlotDrawerProps & SlotDrawerHeaderProps };
    columnHeaderHeight?: number;
    renderDrawer?: { [key: string]: React.ReactNode };
    density?: GridDensity
    verifyBtns?: (row: R, btns: AuthButtonVO[]) => AuthButtonVO[];
    checkboxSelection?: boolean;
    tabs?: React.ReactNode;
    showToolbar?: boolean;
    fieldKey?: string;
    showSearch?: boolean
    source?: { dataSource?: GridDataSource, rowCount?: number },
    disableColumnMenu?: boolean;
    disableColumnResize?: boolean;
    disableColumnSorting?: boolean;
    rowSpanning?: boolean
    customFooter?: boolean;
    noResults?: React.JSXElementConstructor<GridSlotProps['noRowsOverlay' | "noResultsOverlay"]>;
}


export const ProTable = <R extends { id: number | string }, >({
                                                                  code,
                                                                  btnCallback,
                                                                  onSearch,
                                                                  data,
                                                                  hover = true,
                                                                  columnVisibilityModel,
                                                                  rowSpanning = false,
                                                                  showSearch = true,
                                                                  columnHeaderHeight,
                                                                  columns,
                                                                  editCallback,
                                                                  renderDrawer,
                                                                  autoHeight = true,
                                                                  dialogBtnCallback,
                                                                  verifyBtns,
                                                                  viewCallback,
                                                                  loading = false,
                                                                  showToolbar = true,
                                                                  disableColumnMenu = true,
                                                                  disableColumnSorting = true,
                                                                  disableColumnResize = false,
                                                                  rowId = 'id',
                                                                  feet,
                                                                  noResults,
                                                                  fieldKey = 'id',
                                                                  paginationMode = "server",
                                                                  drawerProps = {},
                                                                  tabs,
                                                                  density = 'standard',
                                                                  source,
                                                                  checkboxSelection = true
                                                              }: ProTableProps<R>) => {
        const {setToast} = useGlobalActions();
        const gridApiRef: RefObject<GridApiCommunity | null> = useGridApiRef();
        const operateButtons = useGetAuthButtons(code!, [2, 6, 7]);
        const drawerButtons = useGetAuthButtons(code!, [8]);
        const {setDrawerModal} = useGlobalActions();
        const [openTipBox, closeTipBox] = useTipBox();
        const deletes = useDeletes();
        const searchParam = useGetSearchParam(code!)

        const searchForm: UseFormReturn = useForm({values: searchParam});
        const {size, list, total = 0, current} = data || {};
        const defaultPagination = defaultPageParams

        useEffect(() => {
            if (onSearch) {
                delayLoad(defaultPagination, onSearch)
            }
        }, []);

        const delayLoad = debounce((params, func: (params: any) => void) => {
            return func({...searchParam, ...params})
        }, 0);

        const handleOperate = async (
            btn: AuthButtonVO,
            keys: number[],
            rows: R[],
        ): Promise<void> => {
            switch (btn.alias) {
                case "add":
                    setDrawerModal({
                        open: true,
                        header: btn.remark,
                        write: btn.alias,
                        id: keys[0],
                        btns: [],
                        body: renderDrawer!["write"],
                        onSearch: () => onSearch!(defaultPagination)
                    });
                    return;
                case "edit":
                    if (editCallback) {
                        editCallback({btn, keys, rows, onSearch})
                    } else {
                        setDrawerModal({
                            open: true,
                            id: keys[0],
                            write: btn.alias,
                            header: btn.remark,
                            body: renderDrawer!["write"],
                            onSearch: () => onSearch!({...defaultPagination, ...searchParam})
                        });
                    }
                    return;
                case "view":
                    if (viewCallback) {
                        viewCallback({btn, keys, rows, onSearch})
                    } else {
                        const viewDrawer = drawerProps!["view"]
                        setDrawerModal({
                            open: true,
                            header: btn.remark,
                            onSearch: () => onSearch!(defaultPagination),
                            btns: verifyBtns && verifyBtns(rows[0], drawerButtons) || drawerButtons,
                            dialogBtnCallback: (btn) => dialogBtnCallback && dialogBtnCallback({btn, keys, rows, onSearch}),
                            id: keys[0],
                            body: renderDrawer && renderDrawer![btn.alias],
                            ...(viewDrawer?.viewBtnCallback && viewDrawer.viewBtnCallback!(rows[0])), ...viewDrawer,
                        });
                    }
                    return;
                case "delete":
                    if (keys.length <= 0) {
                        setToast({message: "请先选择要删除的记录!", severity: "warning", open: true});
                        return;
                    }
                    openTipBox({
                        children: (
                            <>
                                <DialogTitle>是否删除该条数据!</DialogTitle>
                                <DialogActions>
                                    <Button color="primary" onClick={() => closeTipBox()}>
                                        取消
                                    </Button>
                                    <Button
                                        color="error"
                                        onClick={async () => {
                                            const ok = await deletes({
                                                url: btn.path,
                                                ids: keys,
                                            });
                                            if (ok) {
                                                closeTipBox();
                                                onSearch!(defaultPagination);
                                            }
                                        }}
                                    >
                                        确定
                                    </Button>
                                </DialogActions>
                            </>
                        ),
                    });
                    return;
            }
            btnCallback && btnCallback({btn, keys, rows, onSearch: () => delayLoad({...searchParam, size, current}, onSearch!)});
        };

        const mergeColumns: GridColDef<R>[] = useMemo(() => {
            const newColumns: GridColDef<R>[] = [
                ...columns,
            ];
            if (!isEmpty(operateButtons)) {
                newColumns.push({
                    field: "actions",
                    type: "actions",
                    headerName: "操作",
                    width: 80,
                    rowSpanValueGetter: (value, row,) => row?.id,
                    getActions: ({row}: GridRowParams) =>
                        operateButtons
                            ?.sort((a: AuthButtonVO, b: AuthButtonVO) => a.sort - b.sort)
                            ?.map((item: AuthButtonVO, index: number) => {
                                return (
                                    <GridActionsCellItem
                                        label={item.menuName}
                                        showInMenu
                                        key={index}
                                        onClick={() => handleOperate(item, [row[fieldKey]], [row])}
                                    />
                                );
                            }),
                })
            }
            return newColumns;
        }, [columns, operateButtons, handleOperate]);

        const handleToolSelect = async (btn: AuthButtonVO) => {
            const keys: number[] = [];
            const rows: R[] = [];
            gridApiRef?.current?.getSelectedRows().forEach((value, key) => {
                keys.push(key as number);
                rows.push(value as R);
            });
            await handleOperate(btn, keys, rows);
        };

        return (
            <Box minHeight={200} {...(autoHeight || total != 0 || (!isEmpty(source) && source?.rowCount != 0)) ? {} : {height: 220}} width={'100%'}
                 paddingY={!isEmpty(code) || !showToolbar ? 0 : 2}
                 maxWidth={{sm: "100%", md: "1700px"}}>
                <DataGrid
                    columnVisibilityModel={columnVisibilityModel}
                    checkboxSelection={checkboxSelection}
                    apiRef={gridApiRef}
                    rows={list || []}
                    rowSpanning={rowSpanning}
                    // onRowClick={async (params: GridRowParams) => {
                    //     const btn = find(operateButtons, (item: AuthButtonVO) => item.alias == "view")!
                    //     btn && await handleOperate(btn,
                    //         [params.row[fieldKey]],
                    //         [params.row],
                    //     );
                    // }}
                    disableColumnMenu={disableColumnMenu}
                    disableColumnResize={disableColumnResize}
                    disableColumnSorting={disableColumnSorting}
                    density={density}
                    loading={loading}
                    rowCount={total}
                    onPaginationModelChange={(model: GridPaginationModel) => {
                        onSearch && onSearch({
                            ...searchParam,
                            current: model.page + 1,
                            size: model.pageSize,
                        })
                    }
                    }
                    hideFooter={feet?.hidden}
                    paginationMode={paginationMode}
                    columns={mergeColumns}
                    getRowClassName={(params: GridRowClassNameParams<R>) =>
                        params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
                    }
                    paginationModel={{
                        page: current! - 1 || 0,
                        pageSize: size || defaultPagination.size,
                    }}
                    dataSource={source?.dataSource}
                    initialState={{
                        pagination: {
                            paginationModel: {
                                pageSize: defaultPagination.size,
                                page: defaultPagination.current,
                            },
                        },
                    }}
                    pageSizeOptions={[20, 30, 50]}
                    // disableColumnResize
                    sx={{
                        '& .MuiDataGrid-row:hover': {
                            backgroundColor: hover ? '' : 'transparent',
                        }
                    }}
                    showToolbar={showToolbar}
                    slots={Object.assign(!isEmpty(code) ? {
                        toolbar: ToolbarContainer as GridSlots['toolbar']
                    } : {}, {
                        ...Object.assign(feet?.custom ? {footer: FooterContainer as GridSlots['footer']} : {}, {}),
                        noRowsOverlay: (typeof noResults == 'function' ? noResults : NoResultsOverlay) as GridSlots['noRowsOverlay'],
                        noResultsOverlay: (typeof noResults == 'function' ? noResults : NoResultsOverlay) as GridSlots['noResultsOverlay'],
                    })
                    }
                    getRowId={(row: any) => row[rowId]}
                    columnHeaderHeight={columnHeaderHeight}
                    disableRowSelectionOnClick={true}
                    slotProps={{
                        loadingOverlay: {
                            variant: "linear-progress",
                            noRowsVariant: "skeleton",
                        },
                        toolbar: {
                            handleToolSelect,
                            code,
                            tabs,
                            showSearch,
                            searchForm: searchForm,
                            onSearch: (params) => onSearch!({...defaultPagination, ...params}),
                        },
                        footer: {
                            handleToolSelect,
                            onSearch: (params) => onSearch!({...defaultPagination, ...params}),
                            code,
                            feet,
                        }
                    }}
                />
            </Box>
        );
    }
;
